import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import useSEO from "../hooks/useSEO";

/*
 * Page SEO dédiée à la marque "YONO".
 * Accessible uniquement via Google (non liée dans la navigation visible).
 * Objectif : renforcer l'association du mot-clé "YONO" avec la marque.
 */
export default function YonoBrand() {
  useSEO({
    title: "YONO | La marque d'habitat bois modulaire conçue en France",
    description:
      "YONO est une marque française d'habitats bois modulaires destinée aux professionnels de l'hôtellerie de plein air, aux campings, PRL, domaines nature et acteurs de l'hébergement. Gamme développée et construite près de Nantes.",
    path: "/yono",
    keywords:
      "YONO, YONO habitat, YONO habitat bois, YONO camping, YONO lodge, YONO studio, YONO HPA, YONO Ideal Tiny, module YONO, marque YONO, habitat modulaire bois, hôtellerie de plein air",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Brand",
      name: "YONO",
      alternateName: ["YONO Habitat", "YONO Habitat Bois"],
      url: "https://yono-habitat.com/yono",
      logo: "https://yono-habitat.com/favicon.svg",
      description:
        "YONO est une marque française d'habitats bois modulaires pour les professionnels de l'hôtellerie de plein air, campings, PRL et domaines nature.",
      slogan: "Habitat bois modulaire pour professionnels HPA",
      sameAs: ["https://yono-habitat.com"],
      makesOffer: [
        { "@type": "Offer", name: "YONO Micro", description: "Micro-habitat bois 6 m²" },
        { "@type": "Offer", name: "YONO Studio", description: "Micro-habitat bois 13 m²" },
        { "@type": "Offer", name: "YONO Nido", description: "Micro-habitat bois familial 13 m²" },
        { "@type": "Offer", name: "YONO Lodge T3", description: "Habitat bois 22 m² alternative mobil-home" },
        { "@type": "Offer", name: "YONO Lodge T4", description: "Grand habitat bois 32 m² familial" },
        { "@type": "Offer", name: "YONO Twin", description: "Habitat bois saisonnier 13 m²" },
      ],
    },
  });

  return (
    <div data-testid="yono-brand-page">
      <section className="pt-20 pb-12">
        <div className="container-yono">
          <p className="eyebrow mb-6">La marque YONO</p>
          <h1 className="font-heading text-[24px] sm:text-[34px] lg:text-[44px] tracking-tight leading-[1.05] max-w-4xl">
            YONO, l'habitat bois modulaire nouvelle génération
          </h1>
          <div className="mt-8 text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/85 max-w-3xl space-y-5">
            <p>
              <strong>YONO</strong> est une gamme française d'habitats bois
              modulaires conçue et fabriquée pour les campings, PRL, domaines
              nature et projets d'hébergement professionnel.
            </p>
            <p>
              La gamme YONO est développée et construite dans les ateliers
              d'<strong>Ideal Tiny</strong>, près de Nantes, avec une approche
              industrielle, sobre et durable. Chaque habitat YONO est pensé pour
              répondre aux besoins concrets des exploitants de l'hôtellerie de
              plein air : densifier intelligemment un parc, remplacer
              progressivement le mobil-home par des hébergements bois, et loger
              les saisonniers directement sur site.
            </p>
            <p>
              YONO propose six solutions structurées en trois familles :
              YONO Micro, YONO Studio, YONO Nido, YONO Lodge T3, YONO Lodge T4
              et YONO Twin.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[var(--yono-beige)] py-14">
        <div className="container-yono">
          <p className="eyebrow mb-5">Pourquoi choisir YONO</p>
          <h2 className="font-heading text-[24px] sm:text-[28px] lg:text-[32px] tracking-tight leading-[1.15] max-w-3xl">
            Une marque française d'habitat bois modulaire pour l'hôtellerie de
            plein air
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6 text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/85">
            <p>
              YONO est conçu en France avec une attention particulière portée à
              la qualité du produit, à la durabilité des matériaux et à
              l&apos;efficacité industrielle de la fabrication. Le coeur de la gamme
              repose sur la technologie <strong>CLT</strong> (bois lamellé-croisé)
              qui donne à chaque habitat YONO sa solidité, son inertie et son
              caractère architectural.
            </p>
            <p>
              Pensés pour les professionnels — campings, parcs résidentiels de
              loisirs, domaines nature, sites touristiques — les habitats YONO
              sont préfabriqués en atelier et livrés prêts à raccorder, ce qui
              limite drastiquement les travaux sur place et accélère la mise en
              exploitation des emplacements.
            </p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-yono">
          <p className="eyebrow mb-5">La gamme YONO</p>
          <h2 className="font-heading text-[24px] sm:text-[28px] lg:text-[32px] tracking-tight leading-[1.15] max-w-3xl">
            Six habitats YONO pour répondre aux besoins de l&apos;hébergement
            professionnel
          </h2>
          <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6 text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/85">
            <li><strong>YONO Micro</strong> — 6 m², mini-hébergement insolite</li>
            <li><strong>YONO Studio</strong> — 13 m², micro-habitat ouvert</li>
            <li><strong>YONO Nido</strong> — 13 m², micro-habitat familial</li>
            <li><strong>YONO Lodge T3</strong> — 22 m², alternative bois au mobil-home</li>
            <li><strong>YONO Lodge T4</strong> — 32 m² et +, grand logement familial</li>
            <li><strong>YONO Twin</strong> — 13 m², habitat saisonnier 2 chambres</li>
          </ul>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/gamme" className="btn-primary" data-testid="yono-brand-cta-gamme">
              Découvrir la gamme YONO <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
            <Link to="/contact" className="btn-outline" data-testid="yono-brand-cta-contact">
              Contacter YONO
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-[var(--yono-beige)] py-14">
        <div className="container-yono">
          <p className="eyebrow mb-5">YONO &amp; Ideal Tiny</p>
          <h2 className="font-heading text-[24px] sm:text-[28px] lg:text-[32px] tracking-tight leading-[1.15] max-w-3xl">
            YONO est fabriqué dans les ateliers d&apos;Ideal Tiny, près de Nantes
          </h2>
          <p className="mt-6 text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/85 max-w-3xl">
            YONO s&apos;appuie sur <strong>Ideal Tiny</strong>, partenaire
            industriel exclusif de la marque, pour la fabrication de ses
            habitats bois. Implantée près de Nantes depuis sept ans, Ideal Tiny
            réunit un atelier spécialisé dans la construction bois, une équipe
            d&apos;une vingtaine de personnes et un bureau d&apos;étude interne. Ce
            partenariat associe l&apos;exigence de conception YONO à la maîtrise
            technique d&apos;un savoir-faire industriel français.
          </p>
        </div>
      </section>
    </div>
  );
}
